import Cookies from 'js-cookie';

/**
 * Token 管理工具函数
 */

const TOKEN_KEY = 'accessToken';
const TOKEN_TYPE_KEY = 'tokenType';
const REFRESH_TOKEN_KEY = 'refreshToken';
const EXPIRES_IN_KEY = 'expiresIn';
const USER_INFO_KEY = 'userInfo';

// Audit 令牌缓存 key（与 auditAuth.js 保持一致，登出时一并清除）
const AUDIT_TOKEN_KEY = 'auditAccessToken';
const AUDIT_TOKEN_EXPIRES_KEY = 'auditTokenExpiresAt';

/**
 * 获取访问令牌
 * @returns {string|null} Access Token
 */
export function getToken() {
  return localStorage.getItem(TOKEN_KEY) || Cookies.get(TOKEN_KEY);
}

/**
 * 设置访问令牌
 * @param {string} token - Access Token
 * @param {number} days - 过期天数（可选，默认为会话期间）
 */
export function setToken(token, days) {
  const options = days ? { expires: days } : {};
  Cookies.set(TOKEN_KEY, token, options);
  localStorage.setItem(TOKEN_KEY, token);
}

/**
 * 移除访问令牌
 */
export function removeToken() {
  Cookies.remove(TOKEN_KEY);
  Cookies.remove(TOKEN_TYPE_KEY);
  Cookies.remove(REFRESH_TOKEN_KEY);
  Cookies.remove(EXPIRES_IN_KEY);
  Cookies.remove(USER_INFO_KEY);
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(TOKEN_TYPE_KEY);
  localStorage.removeItem(REFRESH_TOKEN_KEY);
  localStorage.removeItem(EXPIRES_IN_KEY);
  localStorage.removeItem(USER_INFO_KEY);
  // 清除 Audit 令牌缓存
  localStorage.removeItem(AUDIT_TOKEN_KEY);
  localStorage.removeItem(AUDIT_TOKEN_EXPIRES_KEY);
}

/**
 * 保存完整的认证信息
 * @param {object} authData - 认证数据
 * @param {string} authData.accessToken - 访问令牌
 * @param {string} authData.tokenType - 令牌类型
 * @param {number} authData.expiresIn - 过期时间（秒）
 * @param {string} authData.refreshToken - 刷新令牌
 * @param {boolean} rememberMe - 是否记住登录
 */
export function saveAuthData(authData, rememberMe = false) {
  const { accessToken, tokenType, expiresIn, refreshToken } = authData;
  
  const cookieOptions = rememberMe ? { expires: 7 } : {}; // 7天或会话期间
  
  // 保存到 Cookies
  Cookies.set(TOKEN_KEY, accessToken, cookieOptions);
  if (tokenType) {
    Cookies.set(TOKEN_TYPE_KEY, tokenType, cookieOptions);
  }
  if (refreshToken) {
    Cookies.set(REFRESH_TOKEN_KEY, refreshToken, cookieOptions);
  }
  
  // 保存到 localStorage
  localStorage.setItem(TOKEN_KEY, accessToken);
  localStorage.setItem(TOKEN_TYPE_KEY, tokenType || 'Bearer');
  localStorage.setItem(EXPIRES_IN_KEY, expiresIn || 3600);
  if (refreshToken) {
    localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);
  }
}

/**
 * 保存用户信息（从 JWT payload 提取的精简信息）
 * @param {object} userInfo - { sub, accountId, userId, email, iat, exp }
 * @param {boolean} rememberMe - 是否记住登录（影响 Cookies 过期时间）
 */
export function saveUserInfo(userInfo, rememberMe = false) {
  console.log("111111");
  localStorage.setItem(USER_INFO_KEY, userInfo);
  try {
    const safeInfo = JSON.stringify(userInfo || {});
    const cookieOptions = rememberMe ? { expires: 7 } : {};
    // 仅在信息较小时写入 Cookie（方便 SSR/子域共享等场景），同时写入 localStorage
    Cookies.set(USER_INFO_KEY, safeInfo, cookieOptions);
    localStorage.setItem(USER_INFO_KEY, safeInfo);
  } catch (e) {
    // Fallback: 只写入 localStorage
    try { localStorage.setItem(USER_INFO_KEY, JSON.stringify(userInfo || {})); } catch (_) {}
  }
}

/**
 * 获取用户信息
 * @returns {object|null}
 */
export function getUserInfo() {
  const raw = localStorage.getItem(USER_INFO_KEY) || Cookies.get(USER_INFO_KEY);
  if (!raw) return null;
  try {
    return JSON.parse(raw);
  } catch (e) {
    return null;
  }
}

/**
 * 清除用户信息
 */
export function removeUserInfo() {
  Cookies.remove(USER_INFO_KEY);
  localStorage.removeItem(USER_INFO_KEY);
}

/**
 * 清理会话级缓存
 * 退出登录时一并清除页面临时状态，避免残留上一个账号的数据
 */
export function clearSessionCache() {
  try {
    sessionStorage.clear();
  } catch (e) {
    // ignore
  }
}

/**
 * 获取令牌类型
 * @returns {string} Token Type (默认 'Bearer')
 */
export function getTokenType() {
  return localStorage.getItem(TOKEN_TYPE_KEY) || Cookies.get(TOKEN_TYPE_KEY) || 'Bearer';
}

/**
 * 获取刷新令牌
 * @returns {string|null} Refresh Token
 */
export function getRefreshToken() {
  return localStorage.getItem(REFRESH_TOKEN_KEY) || Cookies.get(REFRESH_TOKEN_KEY);
}

/**
 * 获取 Authorization Header 值
 * @returns {string|null} Authorization header value (e.g., 'Bearer token...')
 */
export function getAuthHeader() {
  console.log('getAuthHeader', getToken());
  const token = getToken();
  if (!token) return null;
  
  const tokenType = getTokenType();
  return `${tokenType} ${token}`;
}

/**
 * 解码 JWT 载荷（不做签名校验，仅用于读取 exp 等声明）
 * @param {string} token
 * @returns {object|null}
 */
export function decodeJwtPayload(token) {
  try {
    if (!token || typeof token !== 'string') return null;
    const parts = token.split('.');
    if (parts.length < 2) return null;
    const payload = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const pad = payload.length % 4;
    const base64 = pad ? payload + '='.repeat(4 - pad) : payload;
    const json = decodeURIComponent(
      atob(base64)
        .split('')
        .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );
    return JSON.parse(json);
  } catch (e) {
    return null;
  }
}

/**
 * 检查用户是否已登录
 * 既校验 token 是否存在，也校验 JWT 是否已过期
 * @returns {boolean}
 */
export function isLoggedIn() {
  const token = getToken();
  if (!token) return false;

  if (typeof token === 'string' && token.split('.').length === 3) {
    const payload = decodeJwtPayload(token);
    if (payload && typeof payload.exp === 'number') {
      // JWT exp 单位为秒
      if (payload.exp * 1000 <= Date.now()) {
        return false;
      }
    }
  }

  return true;
}

/**
 * 登出 - 清除所有认证信息
 */
export function logout() {
  removeToken();
  removeUserInfo();
  clearSessionCache();
}
